/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Live Output Preview Panel
   Renders real GPU texture / color / number outputs from graph execution.
   Includes subtle professional transparency checkerboard, Fit / 100% zoom,
   and live dimensions readout.
   ═══════════════════════════════════════════════════════════════ */

import React, { useRef, useEffect, useState } from 'react';
import { Eye, X } from 'lucide-react';
import type { TextureHandle } from '../../rendering/types';
import { getTextureFromMedia } from '../../rendering/media';
import { useDomainBridge } from '../../stores/domainBridge';
import { useSelectionStore } from '../../stores/selectionStore';
import { useTransientStore } from '../../stores/transientStore';
import { useViewportStore } from '../../stores/viewportStore';
import styles from './OutputPreviewPanel.module.css';

export const OutputPreviewPanel: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoomMode, setZoomMode] = useState<'fit' | '100%'>('fit');

  const { domainState, nodeOutputs, scheduler, isExecuting } = useDomainBridge();
  const { selectedNodeIds } = useSelectionStore();
  const { monitoredPreviewNodeId, setMonitoredPreviewNodeId } = useTransientStore();
  const { previewOpen, setPreviewOpen } = useViewportStore();

  // Determine which node to display:
  // 1. Explicitly monitored node
  // 2. Or first node of type 'output.preview'
  // 3. Or first selected node
  let targetNodeId = monitoredPreviewNodeId;

  if (!targetNodeId) {
    const previewNode = Object.values(domainState.canonical.nodes).find(
      (n) => n.type === 'output.preview'
    );
    if (previewNode) {
      targetNodeId = previewNode.id;
    } else {
      targetNodeId = Array.from(selectedNodeIds)[0] || null;
    }
  }

  const targetNode = targetNodeId ? domainState.canonical.nodes[targetNodeId] : null;
  const outputs = targetNodeId ? nodeOutputs[targetNodeId] : null;

  // Extract primary visual payload
  let texture: TextureHandle | null = null;
  let colorVal: string | null = null;
  let numberVal: number | null = null;

  if (outputs) {
    for (const val of Object.values(outputs)) {
      const tex = getTextureFromMedia(val);
      if (tex && tex.nativeHandle) {
        texture = tex;
        break;
      } else if (typeof val === 'string' && val.startsWith('#')) {
        colorVal = val;
      } else if (typeof val === 'number') {
        numberVal = val;
      }
    }
  }

  // Draw GPU texture to canvas
  useEffect(() => {
    if (previewOpen && texture && canvasRef.current) {
      scheduler.renderToCanvas(texture, canvasRef.current);
    }
  }, [previewOpen, texture, scheduler]);

  if (!previewOpen) return null;

  return (
    <div className={styles.previewContainer}>
      <div className={styles.previewHeader}>
        <div className={styles.headerLeft}>
          <Eye size={13} color="var(--accent)" />
          <span>Studio Stream</span>
          <span className={styles.streamBadge}>
            {targetNode ? targetNode.meta?.label || targetNode.type : 'No Signal'}
          </span>
          {isExecuting && (
            <span style={{ fontSize: '10px', color: '#f59e0b' }}>Processing...</span>
          )}
        </div>

        <div className={styles.headerRight}>
          <button
            className={`${styles.zoomButton} ${zoomMode === 'fit' ? styles.active : ''}`}
            onClick={() => setZoomMode('fit')}
            title="Fit to window"
          >
            Fit
          </button>
          <button
            className={`${styles.zoomButton} ${zoomMode === '100%' ? styles.active : ''}`}
            onClick={() => setZoomMode('100%')}
            title="Actual 100% pixel scale"
          >
            100%
          </button>
          <button
            style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-tertiary)', padding: 0 }}
            onClick={() => setPreviewOpen(false)}
            title="Close preview"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className={`${styles.previewBody} ${styles.checkerboard}`}>
        {texture ? (
          <canvas
            ref={canvasRef}
            className={`${styles.previewCanvas} ${zoomMode === 'fit' ? styles.canvasFit : ''}`}
            style={zoomMode === '100%' ? { transform: 'scale(1)' } : undefined}
          />
        ) : colorVal ? (
          <div className={styles.colorBox} style={{ backgroundColor: colorVal }}>
            <span style={{ background: 'rgba(0,0,0,0.6)', padding: '4px 10px', borderRadius: '4px' }}>
              {colorVal}
            </span>
          </div>
        ) : numberVal !== null && numberVal !== undefined ? (
          <div className={styles.numberBox}>
            <span className={styles.numberValue}>{numberVal.toFixed(3)}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Scalar Output</span>
          </div>
        ) : (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
            {targetNode ? (targetNode.bypassed ? 'Node is bypassed' : 'No active output') : 'Select a node'}
          </div>
        )}
      </div>

      <div className={styles.previewFooter}>
        <span>
          {texture
            ? `${texture.width} × ${texture.height} RGBA WebGL`
            : colorVal
            ? 'sRGB Hex Color'
            : numberVal !== null && numberVal !== undefined
            ? 'Float32 Number'
            : 'Standby'}
        </span>
        {monitoredPreviewNodeId && (
          <button
            style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}
            onClick={() => setMonitoredPreviewNodeId(null)}
          >
            Clear Pin
          </button>
        )}
      </div>
    </div>
  );
};
